import { useParamPage } from '@integration-layer/composables/useGAEvent/useDatalayerSetup/useParamPage'
import type { PageHierarchyNode } from '@integration-layer/ampliance/schemas/page-hierarchy-node-schema.localized'

/**
 * Processes page hierarchy data for Google Analytics tracking.
 *
 * @param {PageHierarchyNode} data - The page hierarchy node containing content and schema information.
 * @returns {void}
 *
 * @description This composable extracts brand, gender, and category information from
 * a page hierarchy node and sets up Google Analytics tracking parameters.
 *
 * @example
 * // Track a page hierarchy for analytics
 * useGAHierarchyPage(pageHierarchyNodeData)
 *
 * @throws {Error} If the data schema is missing or content is invalid.
 */
export const useGAHierarchyPage = (data: PageHierarchyNode) => {
  // Extract brand from the first content item
  // @ts-ignore - Suppressing type checking for content access
  const brand = data.content[0].brand as string

  // Early return if schema is not available
  if (!data.schema) {
    return
  }

  // Extract the last part of the schema path
  const schemaArraySplitted = data.schema.split('/')
  const amplienceSchema = schemaArraySplitted[schemaArraySplitted.length - 1]

  const categoryHierarchyList: string[] =
    // @ts-ignore - Suppressing type checking for content access
    (data.content[0]?.categoryId as string | undefined)
      ?.split('>')
      .map(el => el.trim()) ?? []

  // Extract gender from categoryId
  const gender =
    categoryHierarchyList.find(category =>
      ['man', 'woman'].includes(category)
    ) ?? ''

  // Extract categories, defaulting to an empty array if not available
  // @ts-ignore - Suppressing type checking for content access
  const categories = getCategoriesFromCategoryId(categoryHierarchyList)

  // Set up Google Analytics page tracking parameters
  useParamPage('ampliencePage', {
    pageType: amplienceSchema,
    brand,
    gender,
    categories,
  })
}

const getCategoriesFromCategoryId = (categoriesCompleteList: string[]) => {
  if (!categoriesCompleteList.length) return []

  const isGenderedCategoryPage = categoriesCompleteList.some(
    category => category === 'man' || category === 'woman'
  )

  // non-gendered pages, only have brand and categories
  if (!isGenderedCategoryPage) {
    // some pages, like new-arrivals/kids, have different order of categories
    const [_brand, ...categories] = categoriesCompleteList
    return categories
  }

  // for gendered pages, always consider categories without the gender segment
  const [_brand, ...categoriesWithGender] = categoriesCompleteList

  const categoriesWithoutGender = categoriesWithGender.filter(
    el => !['man', 'woman'].includes(el)
  )
  return categoriesWithoutGender
}
